import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import FilterRangeSelector from 'ui-component/filter-range/FilterRangeSelector';
import moment from 'moment';
import { Skeleton, Tooltip, Avatar } from '@mui/material';
import { FaSpinner, FaTimes } from 'react-icons/fa';
import { LuChevronDown, LuChevronUp } from 'react-icons/lu';
import { FaFilter } from 'react-icons/fa6';

import { FaCircleCheck } from 'react-icons/fa6';
import { getCurrencySymbol, getCurrencySymbolV2, numberFormatter } from 'utils/currency';
import { PiInfoFill, PiMicrosoftExcelLogoFill, PiMicrosoftExcelLogo, PiFilePdf } from 'react-icons/pi';
import { MdKeyboardArrowDown, MdOutlineSettingsBackupRestore } from 'react-icons/md';
import * as XLSX from 'xlsx';
import usePageTitle from 'hooks/usePageTitle';
import useIsMobile from 'utils/isMobileUtils';

const index = () => {
    const [setPageTitle] = usePageTitle('Contact Revenue');
    const statusTypes = useSelector((state) => state.main.statusTypes);
    const statusTypeList = statusTypes?.map((item) => ({ id: item.id, name: item.name }));
    const dispatch = useDispatch();
    const selectedCurrency = useSelector((state) => state.main.selectedCurrency);

    const [showFilterDateUI, setShowFilterDateUI] = useState(false);

    const [selectedFilterBy, setSelectedFilterBy] = useState('MONTH');
    const [defaultSelectedStartYear, setDefaultSelectedStartYear] = useState(moment().year());
    const [defaultSelectedEndYear, setDefaultSelectedEndYear] = useState(moment().year());
    const [defaultSelectedStartMonth, setDefaultSelectedStartMonth] = useState(1);
    const [defaultSelectedEndMonth, setDefaultSelectedEndMonth] = useState(moment().month() + 1);

    const [reportData, setReportData] = useState(null);

    const [isLoading, setIsLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);

    const isMobile = useIsMobile();

    const openInNewTab = (url) => {
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.click();
    };


    const processReport = (startYear, endYear, startMonth, endMonth, filterBy) => {
        setIsLoading(true);
        const request = {
            filter_by: filterBy,
            start_year: startYear,
            end_year: endYear,
            start_month: startMonth,
            end_month: endMonth
        }
        
        api.generateContactRevenueSummaryReport(request).then((res) => {
            setIsLoading(false);
            if (res?.status) {
                setReportData(res.data); 
            }
            // Handle the response data as needed
        }).catch((error) => {
            setIsLoading(false);
            console.error('Error fetching Profit and Loss Statement Report:', error);
        });

    }

    const formatMonth = (date) => date.toLocaleString("en-US", { month: "short", year: "numeric" });

    const formatQuarter = (date) => {
        const quarter = Math.floor(date.getMonth() / 3) + 1;
        return `Q${quarter} ${date.getFullYear()}`;
    };

    const formatYear = (date) => date.getFullYear();
    const dateFilterTextValue = useMemo(() => {
        if (!defaultSelectedStartYear || !defaultSelectedEndYear || !defaultSelectedStartMonth || !defaultSelectedEndMonth) return "";
        
        const startDate = new Date(defaultSelectedStartYear, defaultSelectedStartMonth, 0);
        const endDate = new Date(defaultSelectedEndYear, defaultSelectedEndMonth, 0);
        switch (selectedFilterBy) {
            case "MONTH":
            return `${formatMonth(startDate) === formatMonth(endDate) ? formatMonth(startDate) : `${formatMonth(startDate)} - ${formatMonth(endDate)}`}`;

            case "QUARTER":
            return `${formatQuarter(startDate)} - ${formatQuarter(endDate)}`;

            case "YEAR":
                const yearValue = defaultSelectedStartYear === defaultSelectedEndYear ? formatYear(startDate) : `${formatYear(startDate)} - ${formatYear(endDate)}`;
            return yearValue;

            default:
            return "";
        }
    }, [selectedFilterBy, defaultSelectedStartYear, defaultSelectedEndYear, defaultSelectedStartMonth, defaultSelectedEndMonth]);
    
    useEffect(() => {
        processReport(moment().year(), defaultSelectedEndYear, '1', defaultSelectedEndMonth, selectedFilterBy);
    }, []);


    const s2ab = (s) => {
        const buf = new ArrayBuffer(s.length);
        const view = new Uint8Array(buf);
        for (let i = 0; i !== s.length; ++i) view[i] = s.charCodeAt(i) & 0xff;
        return buf;
    };

    const exportReportXlsx = () => {
        const request = {
            filter_by: selectedFilterBy,
            start_year: defaultSelectedStartYear,
            end_year: defaultSelectedEndYear,
            start_month: defaultSelectedStartMonth,
            end_month: defaultSelectedEndMonth,
            type: 'excel'
        };

        setIsExporting(true);
        api.generateContactRevenueSummaryReport(request).then((res) => {
            setIsExporting(false);
            // 1️⃣ Format API data into Excel table
            const excelData = formatContactRevenueForExcel(res?.data);

            // 2️⃣ Create workbook & worksheet
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.aoa_to_sheet(excelData);

            // 3️⃣ Append sheet
            XLSX.utils.book_append_sheet(wb, ws, 'Contact Revenue');

            // 4️⃣ Export
            const wbBinaryString = XLSX.write(wb, {
                bookType: 'xlsx',
                type: 'binary'
            });

            const blob = new Blob(
                [s2ab(wbBinaryString)],
                { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
            );

            saveAs(blob, 'Projectmix Contact Revenue Report.xlsx');
        });
    };

    const formatContactRevenueForExcel = (data) => {

        if (!data) return [];

        const keys = data.keys || [];
        const rows = data.rows || [];
        const totals = data.total || {};

        /* ---------------------------------------------------
        HEADER ROW
        --------------------------------------------------- */
        const table = [
            [
                'Contact Name',
                ...keys.map(item =>
                    selectedFilterBy === "MONTH"
                        ? moment(item, "YYYY-MM").format("MMM YYYY")
                        : item
                )
            ]
        ];

        let currecySymbol = '$';
        if (selectedCurrency === 'usd') {
            currecySymbol = '$';
        } else if (selectedCurrency === 'eur') {
            currecySymbol = '€';
        } else if (selectedCurrency === 'aed') {
            currecySymbol = 'AED';
        } else {
            currecySymbol = '$';
        }
        
        /* ---------------------------------------------------
        CONTACT ROWS
        --------------------------------------------------- */
        rows.forEach(contact => {

            table.push([
                contact.contact_name,
                ...keys.map(key =>
                    `${currecySymbol} ${(contact.periods?.[key]?.[selectedCurrency === 'all' ? 'usd' : selectedCurrency] || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                )
            ]);
        });

        /* ---------------------------------------------------
        TOTAL ROW
        --------------------------------------------------- */
        table.push([
            'Total',
            ...keys.map(key =>
                `${currecySymbol} ${(totals?.[key]?.[selectedCurrency === 'all' ? 'usd' : selectedCurrency] || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            )
        ]);

        return table;
    };


    const [showSalesIncomeCard, setShowSalesIncomeCard] = useState(false);
    const [showTotalRevenueCard, setShowTotalRevenueCard] = useState(false);
    const [showPurchaseOrdersCard, setShowPurchaseOrdersCard] = useState(false);
    const [showShippingCostsCard, setShowShippingCostsCard] = useState(false);
    const [showTotalExpensesCard, setShowTotalExpensesCard] = useState(false);
    const [showNetProfitCard, setShowNetProfitCard] = useState(false);

    return (
        <>
            <div className="row justify-content-center m-0">
                <div className="medium-sized-container" style={{ maxWidth: '900px' }}>
                    
                    <div className="col-xl-12 col-lg-12 p-0">
                        <div className="show-on-mobile new-page-header" style={{ position: 'relative' }}>
                            <div className="header-bubble" />
                            <div className="d-flex flex-column gap-2 " style={{ position: 'relative', zIndex: 1, paddingTop: 10, width: '100%' }}>
                                <h5 style={{ textAlign: 'center' }}>Contact Revenue</h5>


                                <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                                    <button
                                        className="btn btn-primary btn-outlined-main white-var"
                                        onClick={() => {
                                            if (!isLoading) {
                                                setShowFilterDateUI(!showFilterDateUI)
                                            }
                                        }}
                                    >
                                        <span className="menuIcon">
                                            <FaFilter style={{ fontSize: '1em' }} />
                                        </span>
                                        <span>Filter</span>
                                    </button>
                                    <button
                                        className={`btn btn-primary btn-outlined-main white-var ${isExporting ? 'disabled' : ''}`}
                                        onClick={() => {
                                            if (!isExporting) {
                                                exportReportXlsx();
                                            }
                                        }}
                                        disabled={isExporting}
                                    >
                                        {
                                            isExporting ? (
                                                <>
                                                    <span className="menuIcon">
                                                        <FaSpinner className="fa-spin" style={{ fontSize: '1.4em' }} />
                                                    </span>
                                                    <span>Exporting...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <span className="menuIcon">
                                                        <PiMicrosoftExcelLogo style={{ fontSize: '1.4em' }} /> 
                                                    </span>
                                                    <span>Export</span>
                                                </>
                                            )
                                        }
                                    </button>
                                </div>
                            </div>
                        </div>
                        {
                            isMobile && (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: 10,
                                        paddingTop: 0,
                                        paddingBottom: 5,
                                        paddingLeft: 20,
                                        paddingRight: 20
                                    }}
                                    className="show-on-mobile"
                                >
                                    {
                                        showFilterDateUI && (
                                            <FilterRangeSelector
                                                showFilter={showFilterDateUI}
                                                defaultSelectedStartYear={defaultSelectedStartYear}
                                                defaultSelectedEndYear={defaultSelectedEndYear}
                                                defaultSelectedStartMonth={defaultSelectedStartMonth}
                                                defaultSelectedEndMonth={defaultSelectedEndMonth}
                                                onApplyFilter={(startYear, endYear, startMonth, endMonth, filterBy) => {
                                                    setDefaultSelectedStartYear(startYear);
                                                    setDefaultSelectedEndYear(endYear);
                                                    setDefaultSelectedStartMonth(startMonth);
                                                    setDefaultSelectedEndMonth(endMonth);
                                                    setSelectedFilterBy(filterBy);
                                                    setShowFilterDateUI(false);
                                                    processReport(startYear, endYear, startMonth, endMonth, filterBy);
                                                    // Apply your filtering logic here based on selected dates
                                                }}
                                                showFilterBy={selectedFilterBy}
                                                onFilterVisibility={() => {
                                                    setShowFilterDateUI(false);
                                                }}
                                            />
                                        )
                                    }
                                </div>
                            )
                        }
                        <div className="section-head-new pl-0 hide-on-mobile" style={{ margin: '10px 0' }}>
                            <div>
                                <button
                                    className={`btn btn-primary btn-outlined-main ${isLoading ? 'disabled' : ''}`}
                                    style={{
                                        gap: '0',
                                        zIndex: '12'
                                    }}
                                    onClick={() => {
                                        if (!isLoading) {
                                            setShowFilterDateUI(!showFilterDateUI)
                                        }
                                    }}
                                >
                                    Contact revenue for {dateFilterTextValue}
                                    <span className={`button-rotating-arrow ${showFilterDateUI ? 'rotate-180' : ''} `}>
                                        <MdKeyboardArrowDown />
                                    </span>
                                </button>
                                {
                                    showFilterDateUI && (
                                        <FilterRangeSelector
                                            showFilter={showFilterDateUI}
                                            defaultSelectedStartYear={defaultSelectedStartYear}
                                            defaultSelectedEndYear={defaultSelectedEndYear}
                                            defaultSelectedStartMonth={defaultSelectedStartMonth}
                                            defaultSelectedEndMonth={defaultSelectedEndMonth}
                                            onApplyFilter={(startYear, endYear, startMonth, endMonth, filterBy) => {
                                                setDefaultSelectedStartYear(startYear);
                                                setDefaultSelectedEndYear(endYear);
                                                setDefaultSelectedStartMonth(startMonth);
                                                setDefaultSelectedEndMonth(endMonth);
                                                setSelectedFilterBy(filterBy);
                                                setShowFilterDateUI(false);
                                                processReport(startYear, endYear, startMonth, endMonth, filterBy);
                                                // Apply your filtering logic here based on selected dates
                                            }}
                                            showFilterBy={selectedFilterBy}
                                            onFilterVisibility={() => {
                                                setShowFilterDateUI(false);
                                            }}
                                        />
                                    )
                                }
                            </div>
                            <div className="actions">
                                
                                {isLoading ? (
                                    <Dropdown
                                        className="btnDel est-btn iconbtn disabled"
                                        align={'end'}
                                        disabled
                                    >
                                        <Dropdown.Toggle as="span">
                                            <Link
                                                to={''}
                                                className="btn btn-primary btn-outlined-main white-var disabled" disabled
                                                style={{ minWidth: 'unset', gap: '5px' }}
                                            >
                                                <span className="menuIcon">
                                                    <PiMicrosoftExcelLogo style={{ fontSize: '1.4em' }} /> 
                                                </span>
                                                <span>Export</span>
                                            </Link>
                                        </Dropdown.Toggle>
                                    </Dropdown>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (!isExporting) {
                                                exportReportXlsx();
                                            }
                                        }}
                                        className={`btn btn-primary btn-outlined-main ${isExporting ? 'disabled' : ''}`}
                                        disabled={isExporting}
                                    >
                                        {
                                            isExporting ? (
                                                <>
                                                    <span className="menuIcon">
                                                        <FaSpinner className="fa-spin" style={{ fontSize: '1.4em' }} />
                                                    </span>
                                                    <span>Exporting...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <span className="menuIcon">
                                                        <PiMicrosoftExcelLogo style={{ fontSize: '1.4em' }} /> 
                                                    </span>
                                                    <span>Export</span>
                                                </>
                                            )
                                        }
                                    </button>
                                )}
                            </div>
                        </div>
                        <div
                            className={`card shadow-sm  bg-white mb-4 table-card ${isMobile ? 'shorter-table' : ''}`}
                            style={{
                                overflowY: 'auto',
                                height: 'calc(100vh - 134px)',
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#0966ff transparent'
                            }}
                        >
                            
                            <div className="estimation-table-area">
                                <div className="table-side">

                                    {
                                        !isMobile ? (
                                            <>
                                                <div style={{ width: '100%' }}>
                                                    <div className="row">
                                                        <div className="col-5" style={{ padding: '0' }}> 
                                                            <div className="row">
                                                                <div
                                                                    className="col-12"
                                                                    style={{
                                                                        background: '#ebebeb',
                                                                        height: '35px',
                                                                        marginTop: '-10px',
                                                                        borderTopLeftRadius: '10px',
                                                                        marginBottom: '10px',
                                                                        marginLeft: '10px'
                                                                    }}
                                                                >
                                                                </div>
                                                            </div>
                                                            {
                                                                isLoading ? (
                                                                    [...Array(100)].map((__, i) => (
                                                                        <div key={i} className="row">
                                                                            <div className="col-12 form-group" style={{ marginLeft: '15px' }}>
                                                                                <div style={{ width: '80%' }}>
                                                                                    <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ))
                                                                ) : (
                                                                    reportData?.rows?.length > 0 && (
                                                                        reportData?.rows?.map((row, index) => (
                                                                            <div style={{ height: '32px', marginLeft: '15px' }}>

                                                                                <a
                                                                                    href={`contact-revenue/contact?contactId=${row?.contact_id}&filterBy=${selectedFilterBy}&startYear=${defaultSelectedStartYear}&startMonth=${defaultSelectedStartMonth}&endYear=${defaultSelectedEndYear}&endMonth=${defaultSelectedEndMonth}&contactName=${row?.contact_name}`}
                                                                                    style={{ textDecoration: 'none' }}
                                                                                    target="_blank"
                                                                                >
                                                                                    {row?.contact_name}
                                                                                </a>
                                                                            </div>
                                                                        ))
                                                                    )
                                                                )
                                                            }
                                                          
                                                        </div>



                                                        <div
                                                            className="col-7 flex justify-content-end"
                                                            style={{
                                                                background: '#ebebeb',
                                                                lineHeight: '35px',
                                                                marginTop: '-10px',
                                                                borderTopRightRadius: '10px',
                                                                padding: '0',
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    width: '100%',
                                                                    overflowX: 'auto',
                                                                    scrollbarWidth: 'thin',
                                                                    scrollbarColor: '#0966ff transparent',
                                                                    background: '#fff',
                                                                    overflowY: 'clip'
                                                                }}
                                                            >
                                                                <div style={{
                                                                    display: 'grid',
                                                                    gridTemplateColumns: `repeat(${!isLoading ? reportData?.keys?.length : 12}, 120px)`,
                                                                    background: '#ebebeb',
                                                                }}>
                                                                    {
                                                                        isLoading ? (
                                                                            [...Array(12)].map((__, i) => (
                                                                                <div key={i} style={{ background: '#ebebeb' }}>
                                                                                    <div style={{ width: '100px', padding: '8.5px 0' }}>
                                                                                        <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                                    </div>
                                                                                </div>
                                                                            ))
                                                                        ) : (
                                                                            reportData?.keys?.map((month, index) => (
                                                                                <div key={index} style={{ background: '#ebebeb' }}>
                                                                                    {selectedFilterBy === "MONTH"
                                                                                        ? moment(month, "YYYY-MM").format("MMM YYYY") 
                                                                                        : month}
                                                                                </div>
                                                                            ))
                                                                        )
                                                                    }
                                                                </div>

                                                                    {
                                                                        isLoading ? (

                                                                            [...Array(100)].map((__, i) => (

                                                                                <div style={{
                                                                                    display: 'grid',
                                                                                    gridTemplateColumns: `repeat(${!isLoading ? reportData?.keys?.length : 12}, 120px)`,
                                                                                    background: '#fff',
                                                                                    paddingTop: '5px',
                                                                                    height: '32px'
                                                                                }}>
                                                                                    {[...Array(12)].map((__, i) => (
                                                                                        <div key={i}>
                                                                                            <div style={{ width: '100px', padding: '8.5px 0' }}>
                                                                                                <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                                            </div>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            ))
                                                                        ) : (
                                                                                
                                                                                reportData?.rows?.length > 0 && (
                                                                                    reportData?.rows?.map((row, index) => (
                                                                                        <div style={{
                                                                                            display: 'grid',
                                                                                            gridTemplateColumns: `repeat(${!isLoading ? reportData?.keys?.length : 12}, 120px)`,
                                                                                            background: '#fff',
                                                                                            paddingTop: '5px',
                                                                                            height: '32px',
                                                                                            // marginBottom: '5px'
                                                                                        }}>
                                                                                            {reportData?.keys?.map((monthKey) => (
                                                                                                <div key={monthKey} style={{ minWidth: 100, position: 'relative' }}>
                                                                                                    {/* {
                                                                                                        row?.periods?.[monthKey]?.usd > 0 ? (
                                                                                                            `$ ${row?.periods?.[monthKey]?.usd?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? '0.00'}`
                                                                                                        ) : (
                                                                                                            <span class="text-gray-600">$ 0.00</span>
                                                                                                        ) 
                                                                                                    } */}
                                                                                                    
                                                                                                    <div
                                                                                                        className="flex gap-1"
                                                                                                        style={{
                                                                                                            position: 'absolute',
                                                                                                            marginTop: '-3px'
                                                                                                        }}
                                                                                                    >
                                                                                                        <div 
                                                                                                            style={{
                                                                                                                marginTop: `${selectedCurrency === 'aed' ? '-1px' : '0'}`,
                                                                                                                color: row?.periods?.[monthKey]?.[selectedCurrency === 'all' ? 'usd' : selectedCurrency] > 0 ? '#000' : '#858796'
                                                                                                            }}
                                                                                                        >
                                                                                                            {getCurrencySymbolV2(selectedCurrency === 'all' ? 'usd' : selectedCurrency, 'usd', '11px', '11px')}
                                                                                                        </div>
                                                                                                        <div>
                                                                                                            {
                                                                                                                row?.periods?.[monthKey]?.[selectedCurrency === 'all' ? 'usd' : selectedCurrency] > 0 ? (
                                                                                                                    `${row?.periods?.[monthKey]?.[selectedCurrency === 'all' ? 'usd' : selectedCurrency]?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? '0.00'}`
                                                                                                                ): (
                                                                                                                   <span class="text-gray-600">0.00</span>     
                                                                                                                )
                                                                                                            }
                                                                                                        </div>
                                                                                                    </div>
                                                                                                    
                                                                                                </div>
                                                                                            ))}
                                                                                        </div>
                                                                                    ))
                                                                                )
                                                                        )
                                                                    }
                                                                

                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                            </>
                                        ) : (
                                            <>
                                                 
                                                <div className="inv-cards-list d-md-none" style={{ padding: '0 15px 15px 15px' }}>


                                                    {
                                                        isLoading ? (
                                                          
                                                            [...Array(25)].map((__, i) => (
                                                                <div
                                                                    className="order-list-card"
                                                                    style={{
                                                                        background: '#fff',
                                                                        // marginBottom: 10,
                                                                        borderTopLeftRadius: '12px',
                                                                        borderTopRightRadius: '12px',
                                                                        borderBottomRightRadius: showSalesIncomeCard ? '0px' : '12px',
                                                                        borderBottomLeftRadius: showSalesIncomeCard ? '0px' : '12px',
                                                                        padding: '10px 20px',
                                                                        display: 'flex',
                                                                        flexDirection: 'initial',
                                                                        justifyContent: 'space-between',
                                                                        alignItems: 'center',
                                                                        gap: 10, 
                                                                        border: '1px solid #dfdfdf'
                                                                    }}
                                                                    key={i}
                                                                >
                                                                    <div>
                                                                        <div style={{ width: '200px' }}>
                                                                            <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                        </div>
                                                                    </div>
                                                                    <div>
                                                                        
                                                                        <div
                                                                            className={`invoice-attachment-collapse-icon`}
                                                                            style={{
                                                                                height: '17px', 
                                                                                fontSize: '25px', 
                                                                                background: '#fff'
                                                                            }}
                                                                        >
                                                                            <LuChevronDown />
                                                                        </div>
                                                                    </div>
                                                                    
                                                                </div> 
                                                            ))      
                                                        ) : (
                                                            reportData?.rows?.map((row, rowIdx) => (
                                                                <div key={rowIdx}>
                                                                    <div
                                                                        className="order-list-card"
                                                                        style={{
                                                                            background: '#fff',
                                                                            borderTopLeftRadius: '12px',
                                                                            borderTopRightRadius: '12px',
                                                                            borderBottomRightRadius: showSalesIncomeCard === rowIdx ? '0px' : '12px',
                                                                            borderBottomLeftRadius: showSalesIncomeCard === rowIdx ? '0px' : '12px',
                                                                            padding: '10px 20px',
                                                                            display: 'flex',
                                                                            flexDirection: 'initial',
                                                                            justifyContent: 'space-between',
                                                                            alignItems: 'center',
                                                                            gap: 10,
                                                                            border: '1px solid #dfdfdf',
                                                                            marginBottom: 10
                                                                        }}
                                                                        onClick={() => {
                                                                            if (!isLoading) {
                                                                                setShowSalesIncomeCard(showSalesIncomeCard === rowIdx ? null : rowIdx);
                                                                            }
                                                                        }}
                                                                    >
                                                                        <div>
                                                                            {row.contact_name}
                                                                        </div>
                                                                        <div>
                                                                            <div
                                                                                className={`invoice-attachment-collapse-icon ${showSalesIncomeCard === rowIdx ? 'collapsed' : ''}`}
                                                                                style={{
                                                                                    height: '17px',
                                                                                    fontSize: '25px',
                                                                                    background: '#fff'
                                                                                }}
                                                                            >
                                                                                <LuChevronDown />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    {showSalesIncomeCard === rowIdx && (
                                                                        <div
                                                                            className="order-list-card"
                                                                            style={{
                                                                                background: '#fff',
                                                                                padding: 16,
                                                                                gap: 10,
                                                                                border: '1px solid #dfdfdf',
                                                                                borderBottomLeftRadius: '12px',
                                                                                borderBottomRightRadius: '12px',
                                                                                marginBottom: 10
                                                                            }}
                                                                        >
                                                                            {isLoading ? (
                                                                                [...Array(5)].map((__, i) => (
                                                                                    <div className="flex align-items-center justify-between mb-1" key={i}>
                                                                                        <div style={{ width: '100px' }}>
                                                                                            <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                                        </div>
                                                                                        <div style={{ width: '100px' }}>
                                                                                            <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                                        </div>
                                                                                    </div>
                                                                                ))
                                                                            ) : (
                                                                                reportData?.keys?.map((month, index) => (
                                                                                    <div className="flex align-items-center justify-between mb-1" key={index}>
                                                                                        <div className="text-gray-800" style={{ minWidth: '110px' }}>
                                                                                            {selectedFilterBy === "MONTH"
                                                                                                ? moment(month, "YYYY-MM").format("MMMM YYYY")
                                                                                                : month}
                                                                                        </div>

                                                                                        <div>
                                                                                            {
                                                                                                row?.periods?.[month]?.usd !== undefined ? (

                                                                                                    row?.periods?.[month]?.usd > 0 ? (
                                                                                                        `$ ${row?.periods?.[month]?.usd?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? '0.00'}`
                                                                                                    ) : (
                                                                                                        <span class="text-gray-600">$ 0.00</span>
                                                                                                    )
                                                                                                ) : (
                                                                                                    <span className="text-gray-600">$ 0.00</span>
                                                                                                )
                                                                                                    
                                                                                            }
                                                                                        </div>
                                                                                    </div>
                                                                                ))
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )) 
                                                        ) 
                                                    }
                                                        

                                                        
                                                    {/* <div
                                                        className="order-list-card"
                                                        style={{
                                                            background: '#fff',
                                                            // marginBottom: 10,
                                                            borderTopLeftRadius: '12px',
                                                            borderTopRightRadius: '12px',
                                                            borderBottomRightRadius: showSalesIncomeCard ? '0px' : '12px',
                                                            borderBottomLeftRadius: showSalesIncomeCard ? '0px' : '12px',
                                                            padding: '10px 20px',
                                                            display: 'flex',
                                                            flexDirection: 'initial',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            gap: 10, 
                                                            border: '1px solid #dfdfdf'
                                                        }}
                                                        onClick={() => {
                                                            if (!isLoading) {
                                                                setShowSalesIncomeCard(!showSalesIncomeCard)
                                                            }
                                                        }}
                                                    >
                                                        <div>
                                                            Sales Income
                                                        </div>
                                                        <div>
                                                            
                                                            <div
                                                                className={`invoice-attachment-collapse-icon ${showSalesIncomeCard ? 'collapsed' : ''}`}
                                                                style={{
                                                                    height: '17px', 
                                                                    fontSize: '25px', 
                                                                    background: '#fff'
                                                                }}
                                                            >
                                                                <LuChevronDown />
                                                            </div>
                                                        </div>
                                                        
                                                    </div> 
                                                    {
                                                        showSalesIncomeCard && (
                                                            <div
                                                                className="order-list-card"
                                                                style={{
                                                                    background: '#fff',
                                                                    padding: 16,
                                                                    gap: 10, 
                                                                    border: '1px solid #dfdfdf',
                                                                    borderBottomLeftRadius: '12px',
                                                                    borderBottomRightRadius: '12px',
                                                                }}
                                                            >
                                                                {
                                                                    isLoading ? (
                                                                        [...Array(5)].map((__, i) => (
                                                                            <div className="flex align-items-center justify-between mb-1" key={i}>

                                                                                <div style={{ width: '100px' }}>
                                                                                    <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                                </div>
                                                                                <div style={{ width: '100px' }}>
                                                                                    <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                                </div>
                                                                            </div>
                                                                        ))
                                                                    ) : (
                                                                        reportData?.keys?.map((month, index) => (

                                                                            <div className="flex align-items-center justify-between  mb-1" key={index}>

                                                                                <div className="text-gray-800" style={{ minWidth: '110px' }}>
                                                                                    {selectedFilterBy === "MONTH"
                                                                                        ? moment(month, "YYYY-MM").format("MMMM YYYY") 
                                                                                        : month}
                                                                                </div>
                                                                                <div>

                                                                                    {reportData?.revenue?.sales_income[month]?.usd !== undefined
                                                                                        ? `$ ${reportData.revenue.sales_income[month].usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                                                                        : '<span class="text-gray-600">$ 0.00</span>'}
                                                                                </div>
                                                                            </div>
                                                                        ))
                                                                    )
                                                                }
                                                            </div>
                                                        )
                                                    } */}
                                                    

                            

                                                </div>   

                                            </>
                                        )
                                    }
                                    {isLoading && (
                                        !isMobile && (

                                            <div className="inv-cards-list d-md-none" style={{ padding: '12px 0' }}>
                                                {[...Array(15)].map((_, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="order-list-card"
                                                        style={{
                                                            background: idx % 2 === 0 ? '#f9f9f9' : '#fff',
                                                            marginBottom: 10,
                                                            borderRadius: 12,
                                                            padding: 16,
                                                            minHeight: 90,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: 10
                                                        }}
                                                    >
                                                        {[...Array(3)].map((__, i) => (
                                                            <div key={i} style={{ width: i === 0 ? '60%' : i === 1 ? '40%' : '80%' }}>
                                                                <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                            </div>
                                                        ))}
                                                    </div>
                                                ))}
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </>
    );
};

export default index;
